/**
 * @file look-outside.ts
 * @description «Фон за рамку сайта» у секции и счётчик «ваших чисел» отступов
 * (phase19.16, партия SB120, этап 2; site-builder.md SB120; проект
 * docs/phases/phase19.16-materials/uiux-sb120-stage2.md §3 и §2 «Отступы
 * сайта»; данные db-design-sb120-stage2 §1 `look.outsideFrame`).
 *
 * Чистые функции без React:
 *  - `outsideFrameState` — почему поле серое (рамки нет / у блока нет фона),
 *    видна ли разница на текущей ширине канвы, «!» при «Слить с блоком
 *    выше», когда верхний блок остался в рамке; при подложке фон в карточке —
 *    за рамку не выходит (поле серое);
 *  - `prevBlockOutside` — вышел ли за рамку блок выше (по блокам страницы);
 *  - `ownSpaceCount` — сколько своих чисел отступов в блоках страницы:
 *    плотность сайта меняет только «как у сайта», свои числа не трогает.
 */

import type { SectionLook, SiteTheme } from "@chaiberi/shared";

/** Почему «Фон за рамку сайта» недоступен. */
export type OutsideFrameBlock = "frame-full" | "transparent" | "card";

export interface OutsideFrameState {
  /** Причина серого поля; `null` — поле доступно. */
  blocked: OutsideFrameBlock | null;
  /** Ширина рамки сайта «По центру», px. */
  frameWidth: number;
  /** Канва не шире рамки — разницу на ней не видно. */
  narrowCanvas: boolean;
  /** Блок слит с блоком выше, а тот остался в рамке. */
  mergeWarning: boolean;
}

type Frame = Pick<SiteTheme["frame"], "mode" | "maxWidth">;

/** Сырые поля `look`, от которых зависит выход за рамку. */
interface OutsideLookLike {
  outsideFrame?: unknown;
  bg?: unknown;
  card?: unknown;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Выходит ли фон блока за рамку на самом деле (как решает витрина): флаг
 * включён, рамка «По центру», у блока свой фон и нет подложки. Флаг без
 * этих условий хранится, но ничего не меняет — сводки и «!» по нему не судят.
 */
export function isOutsideFrame(look: OutsideLookLike, frame: Pick<Frame, "mode">): boolean {
  if (look.outsideFrame !== true || frame.mode !== "boxed") return false;
  const bgKind = isObject(look.bg) ? look.bg["kind"] : undefined;
  const cardOn = isObject(look.card) ? look.card["on"] === true : false;
  return bgKind !== undefined && bgKind !== "transparent" && !cardOn;
}

/**
 * Состояние поля. `canvasWidth` — ширина канвы (`null` — канвы нет),
 * `prevOutside` — вышел ли за рамку блок выше (`null` — неизвестно или
 * блока выше нет).
 */
export function outsideFrameState(
  look: Pick<SectionLook, "bg" | "space" | "card" | "outsideFrame">,
  frame: Frame,
  canvasWidth: number | null,
  prevOutside: boolean | null,
): OutsideFrameState {
  // Подложка: фон рисуется в карточке, витрина за рамку его не выводит (решение по ревью витрины, SB120 этап 2).
  const blocked: OutsideFrameBlock | null =
    frame.mode === "full" ? "frame-full" : look.bg.kind === "transparent" ? "transparent" : look.card.on ? "card" : null;
  return {
    blocked,
    frameWidth: frame.maxWidth,
    narrowCanvas: canvasWidth !== null && canvasWidth <= frame.maxWidth,
    mergeWarning: isOutsideFrame(look, frame) && look.space.mergeWithPrev && prevOutside === false,
  };
}

/** Блок страницы — столько, сколько нужно для расчётов. */
export interface PageBlockLike {
  id: string;
  sortOrder: number;
  payload: unknown;
}

/** `look` секции из данных блока (любой версии); не секция — `null`. */
function lookOf(payload: unknown): Record<string, unknown> | null {
  if (!isObject(payload)) return null;
  const look = payload["look"];
  return isObject(look) ? look : null;
}

/** Вышел ли за рамку (на самом деле) блок выше `blockId`; блока выше нет или он не найден — `null`. */
export function prevBlockOutside(blocks: readonly PageBlockLike[], blockId: string, frame: Pick<Frame, "mode">): boolean | null {
  const ordered = [...blocks].sort((a, b) => a.sortOrder - b.sortOrder);
  const index = ordered.findIndex((b) => b.id === blockId);
  const prev = index > 0 ? ordered[index - 1] : undefined;
  if (!prev) return null;
  return isOutsideFrame(lookOf(prev.payload) ?? {}, frame);
}

/** Поля отступов секции, которые масштабирует плотность, когда своего числа нет. */
const SPACE_KEYS = ["outerTop", "outerBottom", "innerTop", "innerBottom", "innerLeft", "innerRight"] as const;

/**
 * Сколько своих чисел отступов в блоках (шесть сторон секции и расстояние
 * между колонками). Их плотность сайта не меняет — подпись под «Плотностью».
 */
export function ownSpaceCount(blocks: readonly PageBlockLike[]): number {
  let count = 0;
  for (const block of blocks) {
    const look = lookOf(block.payload);
    const space = look?.["space"];
    if (isObject(space)) for (const key of SPACE_KEYS) if (typeof space[key] === "number") count += 1;
    const columns = isObject(block.payload) ? block.payload["columns"] : undefined;
    if (isObject(columns) && typeof columns["gap"] === "number") count += 1;
  }
  return count;
}
